import React, { useState, useEffect } from 'react';
import { customerAPI } from '../../services';
import { Cake, Mail, Phone, Calendar, Gift, Loader } from 'lucide-react';
import { Button } from '../UI';

const BirthdayWidget = () => {
  const [birthdays, setBirthdays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingWish, setSendingWish] = useState(null);

  useEffect(() => {
    fetchBirthdays();
  }, []);

  const fetchBirthdays = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getUpcomingBirthdays();
      if (response.status === 'success') {
        setBirthdays(response.data.birthdays || []);
      }
    } catch (error) {
      console.error('Failed to fetch birthdays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendWish = async (customerId, customerName) => {
    try {
      setSendingWish(customerId);
      await customerAPI.sendBirthdayWish(customerId);
      alert(`Birthday wish sent to ${customerName}!`);
    } catch (error) {
      console.error('Failed to send birthday wish:', error);
      alert('Failed to send birthday wish');
    } finally {
      setSendingWish(null);
    }
  };

  const formatBirthdayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const getDaysText = (daysUntil) => {
    if (daysUntil === 0) return 'Today! 🎉';
    if (daysUntil === 1) return 'Tomorrow';
    return `In ${daysUntil} days`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <Loader className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl border border-pink-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Cake className="w-5 h-5 text-pink-500" />
          Upcoming Birthdays
        </h3>
        {birthdays.length > 0 && (
          <span className="bg-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            {birthdays.length}
          </span>
        )}
      </div>

      {birthdays.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {birthdays.map((customer) => (
            <div
              key={customer._id}
              className={`p-4 rounded-lg border-2 transition-all ${
                customer.isToday
                  ? 'bg-gradient-to-r from-pink-100 to-purple-100 border-pink-300 shadow-md'
                  : 'bg-white border-pink-100 hover:border-pink-200'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900">{customer.name}</h4>
                    {customer.isToday && (
                      <Gift className="w-4 h-4 text-pink-500 animate-bounce" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <Calendar className="w-3 h-3" />
                    <span className="font-medium">{formatBirthdayDate(customer.dateOfBirth)}</span>
                    <span className="text-pink-600 font-semibold">
                      • {getDaysText(customer.daysUntil)}
                    </span>
                  </div>
                  {customer.location && (
                    <p className="text-xs text-gray-500">
                      {customer.location} {customer.area && `- ${customer.area}`}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-3">
                <a
                  href={`tel:${customer.phone}`}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Phone className="w-3 h-3" />
                  Call
                </a>
                {customer.email && (
                  <button
                    onClick={() => handleSendWish(customer._id, customer.name)}
                    disabled={sendingWish === customer._id}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-medium hover:from-pink-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingWish === customer._id ? (
                      <>
                        <Loader className="w-3 h-3 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-3 h-3" />
                        Send Wish
                      </>
                    )}
                  </button>
                )}
              </div>

              {customer.addedBy && (
                <p className="text-xs text-gray-500 mt-2">
                  Added by: {customer.addedBy.name}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Cake className="w-12 h-12 text-pink-300 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">No upcoming birthdays in the next 7 days</p>
        </div>
      )}
    </div>
  );
};

export default BirthdayWidget;
